import React, { useMemo } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Text } from './AppText';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ThemeColors } from '../theme';
import { useTheme, useThemeMode } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { useUnread } from '../unread/UnreadContext';
import { formatBadgeCount } from '../unread/badge';
import { floatingDockMetrics } from './floatingDockLayout';
import { getGlassView } from './nativeGlass';

type Tab = 'discover' | 'inbox' | 'calls' | 'calendar' | 'profile';

type Props = {
  active: Tab;
  onPress: (tab: Tab) => void;
};

function DiscoverIcon({ active }: { active: boolean }) {
  const colors = useTheme();
  const c = active ? colors.black : colors.gray400;
  return (
    <View style={icon.grid}>
      <View style={[icon.dot, { backgroundColor: c }]} />
      <View style={[icon.dot, { backgroundColor: c }]} />
      <View style={[icon.dot, { backgroundColor: c }]} />
      <View style={[icon.dot, { backgroundColor: c }]} />
    </View>
  );
}

function InboxIcon({ active }: { active: boolean }) {
  const colors = useTheme();
  const c = active ? colors.black : colors.gray400;
  return (
    <View style={[icon.bubble, { borderColor: c }]}>
      <View style={[icon.bubbleTail, { backgroundColor: c }]} />
    </View>
  );
}

// Aramalar (9.4.10). Klasik telefon ahizesi — evrensel "çağrı" simgesi ve
// uygulamanın kendi çağrı ikonuyla (CallScreen'deki Ionicons 'call') aynı.
// Ahize eğrisi View'la temiz çıkmadığından ikon-fontu kullanılıyor; dock'un
// rozet metni ayrı testID ile hedeflendiği için "metin yok" değişmezi rozete
// özgü kalıyor.
function CallsIcon({ active }: { active: boolean }) {
  const colors = useTheme();
  const c = active ? colors.black : colors.gray400;
  return <Ionicons name="call" size={21} color={c} />;
}

export function CalendarIcon({ active, color }: { active: boolean; color?: string }) {
  const colors = useTheme();
  const c = color ?? (active ? colors.black : colors.gray400);
  return (
    <View style={icon.calendarWrap}>
      <View style={icon.calendarRings}>
        <View style={[icon.calendarRing, { backgroundColor: c }]} />
        <View style={[icon.calendarRing, { backgroundColor: c }]} />
      </View>
      <View style={[icon.calendarBody, { borderColor: c }]}>
        <View style={[icon.calendarBand, { backgroundColor: c }]} />
      </View>
    </View>
  );
}

function ProfileIcon({ active }: { active: boolean }) {
  const colors = useTheme();
  const c = active ? colors.black : colors.gray400;
  return (
    <View style={icon.person}>
      <View style={[icon.head, { backgroundColor: c }]} />
      <View style={[icon.body, { backgroundColor: c }]} />
    </View>
  );
}

// Rozet (9.26.7.2): sayı — sohbet satırındaki rozetle tutarlı olsun diye — ve
// iki haneyi geçince "99+". Sayı yalnız görsel kalmasın diye erişilebilirlik
// etiketi sekmenin kendisinde: ekran okuyucu "Mesajlar, 3 okunmamış" der.
function Badge({ count }: { count: number }) {
  const colors = useTheme();
  const { lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  if (count <= 0) return null;
  return (
    <View style={styles.badge} pointerEvents="none" accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
      <Text testID="dock-badge" style={styles.badgeText} numberOfLines={1}>{formatBadgeCount(count, lang)}</Text>
    </View>
  );
}

export default function FloatingDock({ active, onPress }: Props) {
  const insets = useSafeAreaInsets();
  const colors = useTheme();
  const { isDark } = useThemeMode();
  const { t } = useTranslation();
  const { messages, meetups } = useUnread();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const label = (key: string, count: number, countKey: string) =>
    count > 0 ? `${t(key)}, ${t(countKey, { count })}` : t(key);

  /**
   * 7.14 · D-548 — alt çubuk, camın kendini GERÇEKTEN gösterdiği ilk yüzey.
   *
   * Başlık kontrolleri düz bir sayfanın üstünde duruyor, altlarında kıracak
   * bir şey yok (D-547). Bu çubuk ise zaten `position: 'absolute'` ile
   * içeriğin üstünde yüzüyor — listeler altından geçiyor. Yani yerleşim
   * değişmiyor, yalnız dolgu malzemeye bırakılıyor.
   */
  const GlassView = useMemo(() => getGlassView(), []);

  return (
    <View style={[
      GlassView ? styles.nativeDock : styles.dock,
      { bottom: insets.bottom + floatingDockMetrics.bottomOffset },
    ]}>
      {GlassView && (
        /* Malzeme kendi dolgusunu ve gölgesini taşır; bizim beyaz dolgumuz ve
           gölgemiz üstüne binmiyor. Dokunmayı engellememesi için akış dışında. */
        <GlassView
          style={styles.nativeDockSurface}
          glassEffectStyle="regular"
          isInteractive
          colorScheme={isDark ? 'dark' : 'light'}
          pointerEvents="none"
        />
      )}
      <TouchableOpacity
        style={styles.tab}
        onPress={() => onPress('discover')}
        accessibilityRole="button"
        accessibilityLabel={t('dock.discover')}>
        <DiscoverIcon active={active === 'discover'} />
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.tab}
        onPress={() => onPress('inbox')}
        accessibilityRole="button"
        accessibilityLabel={label('dock.inbox', messages, 'dock.unreadMessages')}>
        <InboxIcon active={active === 'inbox'} />
        <Badge count={messages} />
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.tab}
        onPress={() => onPress('calls')}
        accessibilityRole="button"
        accessibilityLabel={t('dock.calls')}>
        <CallsIcon active={active === 'calls'} />
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.tab}
        onPress={() => onPress('calendar')}
        accessibilityRole="button"
        accessibilityLabel={label('dock.calendar', meetups, 'dock.pendingRequests')}>
        <CalendarIcon active={active === 'calendar'} />
        <Badge count={meetups} />
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.tab}
        onPress={() => onPress('profile')}
        accessibilityRole="button"
        accessibilityLabel={t('dock.profile')}>
        <ProfileIcon active={active === 'profile'} />
      </TouchableOpacity>
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  /** Yerel malzemede yalnız ölçü ve yerleşim bizden; boya tamamen sistemin. */
  nativeDock: {
    position: 'absolute',
    alignSelf: 'center',
    flexDirection: 'row',
    paddingVertical: floatingDockMetrics.verticalPadding,
    paddingHorizontal: 12,
    gap: 4,
  },
  nativeDockSurface: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 40,
  },
  dock: {
    position: 'absolute',
    alignSelf: 'center',
    flexDirection: 'row',
    backgroundColor: colors.white,
    borderRadius: 40,
    paddingVertical: floatingDockMetrics.verticalPadding,
    paddingHorizontal: 12,
    gap: 4,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 8,
  },
  tab: {
    alignItems: 'center',
    justifyContent: 'center',
    width: floatingDockMetrics.tabSize,
    height: floatingDockMetrics.tabSize,
  },
  // İkonun sağ üstüne oturur; dokunma hedefini bozmasın diye akıştan çıkarıldı
  // (position: 'absolute' + pointerEvents="none").
  badge: {
    position: 'absolute',
    top: 4,
    right: 2,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    paddingHorizontal: 4,
    backgroundColor: '#e53935',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    color: '#fff',
    fontSize: 10,
    lineHeight: 13,
    fontWeight: '700',
  },
});

const icon = StyleSheet.create({
  grid: {
    width: 18,
    height: 18,
    flexWrap: 'wrap',
    flexDirection: 'row',
    gap: 4,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: 2,
  },
  bubble: {
    width: 20,
    height: 17,
    borderRadius: 6,
    borderWidth: 2,
    justifyContent: 'flex-end',
  },
  bubbleTail: {
    width: 5,
    height: 4,
    borderRadius: 1,
    marginLeft: 3,
    marginBottom: -3,
  },
  person: { alignItems: 'center', gap: 2 },
  head: { width: 10, height: 10, borderRadius: 5 },
  body: { width: 16, height: 8, borderRadius: 8 },
  calendarWrap: { alignItems: 'center' },
  calendarRings: { flexDirection: 'row', gap: 5, marginBottom: -2, zIndex: 1 },
  calendarRing: { width: 2, height: 5, borderRadius: 1 },
  calendarBody: { width: 18, height: 16, borderRadius: 4, borderWidth: 1.5, overflow: 'hidden' },
  calendarBand: { height: 2, width: '100%', marginTop: 3 },
});
